import { useCallback, useMemo } from 'react'
import { useHotkeys, useHotkeysContext } from 'react-hotkeys-hook'
import { COMMENT_ESC_SCOPE_PREFIX } from '@scene'
import { some } from 'lodash-es'
import usePlanImageCardEditMode from '@editor/hooks/usePlanImageCardEditMode'
import usePlanTableEditMode from '@editor/hooks/usePlanTableEditMode'
import { useSnackbar } from '@ui/snackbar'
import {
  useControlStore,
  useElementLoadStore,
  useSelectionStore,
  useStructuralPlanningStore,
  useTapelineStore,
} from '@editorStores'
import { useSelectedPlanElementId, useSelectionMode } from '@editorHooks'
import { useStructuralPlanningSelection } from '@structuralPlanningHooks'
import { STRUCTURAL_PLANNING_DRAWER_ESC_SCOPE_PREFIX } from './useStructuralPlanningDrawerEsc'
import { STRUCTURAL_PLANNING_FORM_ESC_SCOPE_PREFIX } from './useStructuralPlanningFormEsc'

/**
 * Page-level Escape handling. Overlays (context menu, plan element edit, snackbar pick flows,
 * stiffening segment picks, unsaved local loads) resolve first; everything else is one step of
 * the selection store's ladder — sub-selection → selection → mode back to neutral — identical in
 * the floorplan and the 3D view.
 */
const useStructuralPlanningEsc = () => {
  const isTapelineDrawing = useTapelineStore(state => state.isDrawing)
  const actionMode = useControlStore(state => state.actionMode)
  const deselectAllIds = useStructuralPlanningStore(state => state.deselectAllIds)
  const selectedIds = useStructuralPlanningStore(state => state.selectedIds)

  const setContext = useControlStore(state => state.setContextMenu)
  const context = useControlStore(state => state.contextMenu)

  const escape = useSelectionStore(state => state.escape)

  const { enabledScopes } = useHotkeysContext()

  const loads = useElementLoadStore(state => state.loads)

  const {
    modes: { isStiffeningSelectionMode, isLoadMode },
  } = useStructuralPlanningSelection()

  const { isSelectionMode: isSnackbarSelection, unsetSelectionMode } = useSelectionMode()
  const { enqueueSnackbar } = useSnackbar()
  const selectedPlanTableId = useSelectedPlanElementId('table')
  const { exitPlanTableEdit } = usePlanTableEditMode()
  const selectedPlanImageCardId = useSelectedPlanElementId('image-card')
  const { exitPlanImageCardEdit } = usePlanImageCardEditMode()

  const hasLocalLoads = useMemo(() => some(loads, 'isLocal'), [loads])

  const escKeyPress = useCallback(() => {
    if (context) {
      setContext(null)
    } else if (selectedPlanTableId) {
      exitPlanTableEdit()
    } else if (selectedPlanImageCardId) {
      exitPlanImageCardEdit()
    } else if (isSnackbarSelection) {
      unsetSelectionMode()
    } else if (isStiffeningSelectionMode && selectedIds.size) {
      deselectAllIds()
    } else if (isLoadMode && hasLocalLoads) {
      enqueueSnackbar('Noch nicht alle Lasten gespeichert, bitte zuerst speichern oder entfernen', {
        variant: 'info',
      })
    } else {
      escape()
    }
  }, [
    context,
    setContext,
    selectedPlanTableId,
    exitPlanTableEdit,
    selectedPlanImageCardId,
    exitPlanImageCardEdit,
    isSnackbarSelection,
    unsetSelectionMode,
    isStiffeningSelectionMode,
    selectedIds,
    deselectAllIds,
    isLoadMode,
    hasLocalLoads,
    enqueueSnackbar,
    escape,
  ])

  const drawerScopeEnabled = useMemo(
    () =>
      some(enabledScopes, scope => scope.startsWith(STRUCTURAL_PLANNING_DRAWER_ESC_SCOPE_PREFIX)),
    [enabledScopes],
  )

  const formScopeEnabled = useMemo(
    () => some(enabledScopes, scope => scope.startsWith(STRUCTURAL_PLANNING_FORM_ESC_SCOPE_PREFIX)),
    [enabledScopes],
  )

  const commentScopeEnabled = useMemo(
    () => some(enabledScopes, scope => scope.startsWith(COMMENT_ESC_SCOPE_PREFIX)),
    [enabledScopes],
  )

  useHotkeys(
    'esc',
    escKeyPress,
    {
      enabled:
        !isTapelineDrawing &&
        actionMode !== 'hide' &&
        !drawerScopeEnabled &&
        !formScopeEnabled &&
        !commentScopeEnabled,
    },
    [escKeyPress],
  )
}

export default useStructuralPlanningEsc
